import React from 'react';
import _ from 'lodash';
import colors from 'tailwindcss/colors';
import ChartLoader from '@/pages/dashboard/_charts/ChartLoader';
import {partnerResponses, stewardResponses} from './_projectInvitationDeclineReasons';
import {useProjectsContext} from "@/pages/dashboard/ProjectsContext";
import DonutChart from "@/pages/dashboard/_charts/DonutChart";

/**
 * QuestionDeclinerYear Component
 *
 * Displays a donut chart showing the breakdown of whom declined project invitations
 * (partners vs. stewards). It analyzes decline reasons to categorize each decline.
 *
 * @param {Object} props - Component props
 * @param {string} [props.subtitle] - Optional subtitle for the chart
 * @returns {JSX.Element} - A donut chart or loading indicator
 */
export default function QuestionDeclinerYear(props) {
  const {subtitle} = props;
  const {filteredInvitations, isLoadingInvitations} = useProjectsContext();

  // Show loading state while projects are being fetched
  if (isLoadingInvitations) {
    return (
      <ChartLoader
        title="Breakdown of who declined the invitation"
        subtitle={subtitle || 'For school year'}
      />
    );
  }

  // Create regex patterns for matching partner and steward decline reasons
  // These patterns are case-insensitive and properly escaped for regex
  const partnerRespRegex = partnerResponses.map(resp => new RegExp(_.escapeRegExp(resp), "i"));
  const stewardRespRegex = stewardResponses.map(resp => new RegExp(_.escapeRegExp(resp), "i"));

  // Initialize counters for partner and steward declines
  const counts = {partner: 0, steward: 0};

  // Iterate through all projects and their decline reasons
  for (const invitation of filteredInvitations) {
    // Check if the decline reason matches any partner response pattern
    const isPartner = partnerRespRegex.find(regex => regex.test(invitation.responseMessage));
    if (isPartner) counts.partner++;

    // Check if the decline reason matches any steward response pattern
    const isSteward = stewardRespRegex.find(regex => regex.test(invitation.responseMessage));
    if (isSteward) counts.steward++;
  }

  // Chart labels and data series
  const labels = ['Declined by Partner', 'Declined by Steward']
  const series = [counts.partner, counts.steward]

  // Render the donut chart with two color-coded categories
  return (
    <DonutChart
      title="Breakdown of who declined the invitation"
      subtitle={subtitle || 'For school year'}
      labels={labels}
      series={series}
      colors={[
        colors.emerald['400'],  // Green for partner declines
        colors.yellow['400']    // Yellow for steward declines
      ]}
    />
  );
}
